import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { LineChart } from '@mui/x-charts/LineChart'
import { Link as RouterLink } from 'react-router-dom'
import {
  getListenerCountHistory,
  type ListenerCountHistoryPeriod,
} from '../../api/station'
import useListenerCountStore from '../../store/listenerCount'
import useStationListenerCountSettingsStore from '../../store/stationListenerCounts'
import {
  LISTENER_COUNT_REFRESH_INTERVAL_MS,
  formatListenerCountRelativeTime,
} from '../../utils/listenerCount'
import { dashboardCardHeadingTypography } from './typography'

export interface ListenerCountHistoryCardProps {
  stationId: number | null
}

type ChartDatasetEntry = {
  time: number
  [seriesId: string]: number | null | number
}

type ChartSeriesConfig = {
  id: string
  label: string
  dataKey: string
}

type HistorySeriesState = {
  dataset: ChartDatasetEntry[]
  configs: ChartSeriesConfig[]
  lastRefresh: string | null
}

const PERIOD_OPTIONS: Array<{ value: ListenerCountHistoryPeriod; label: string }> = [
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '3d', label: '3d' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
]

const ListenerCountHistoryCard: React.FC<ListenerCountHistoryCardProps> = ({ stationId }) => {
  const theme = useTheme()
  const listenerSettings = useStationListenerCountSettingsStore(
    React.useCallback(
      (state) => (stationId ? state.listenerCounts[stationId] : undefined),
      [stationId],
    ),
  )
  const { lastUpdated, version, nextRefreshAt } = useListenerCountStore((state) => ({
    lastUpdated: state.lastUpdated,
    version: state.version,
    nextRefreshAt: state.nextRefreshAt,
  }))
  const [selectedPeriod, setSelectedPeriod] = React.useState<ListenerCountHistoryPeriod>('24h')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [series, setSeries] = React.useState<HistorySeriesState>(() => ({
    dataset: [],
    configs: [],
    lastRefresh: null,
  }))

  const configLoaded = Boolean(listenerSettings?.loaded)
  const configEnabled = Boolean(listenerSettings?.enabled)
  const configEndpoint = listenerSettings?.endpoint ?? null
  const active = Boolean(stationId && configLoaded && configEnabled && configEndpoint)

  const fetchInFlightRef = React.useRef(false)
  const pendingRef = React.useRef(false)
  const timerRef = React.useRef<number | null>(null)
  const latestParamsRef = React.useRef<{ stationId: number | null; period: ListenerCountHistoryPeriod }>(
    { stationId, period: selectedPeriod },
  )
  const fetchHistoryRef = React.useRef<(() => Promise<void>) | null>(null)
  const lastAppliedUpdateRef = React.useRef<string | null>(null)
  const lastAppliedVersionRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    latestParamsRef.current = { stationId, period: selectedPeriod }
  }, [stationId, selectedPeriod])

  const resetState = React.useCallback(() => {
    setSeries({ dataset: [], configs: [], lastRefresh: null })
    setLoading(false)
    setError(null)
    fetchInFlightRef.current = false
    pendingRef.current = false
  }, [])

  const normalizeCount = React.useCallback((value: number | null | undefined): number | null => {
    if (value === null || value === undefined) {
      return null
    }

    if (typeof value !== 'number') {
      return null
    }

    if (!Number.isFinite(value)) {
      return null
    }

    return Math.max(0, Math.trunc(value))
  }, [])

  const formatTimestamp = React.useCallback((value: number | null | undefined): string => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '—'
    }

    return new Date(value).toLocaleString()
  }, [])

  const formatCount = React.useCallback((value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—'
    }

    return value.toLocaleString()
  }, [])

  const fetchHistory = React.useCallback(async () => {
    if (!active || !stationId) {
      return
    }

    if (fetchInFlightRef.current) {
      pendingRef.current = true
      return
    }

    fetchInFlightRef.current = true
    setLoading(true)
    setError(null)

    const requestStationId = stationId
    const requestPeriod = selectedPeriod

    try {
      const response = await getListenerCountHistory(requestStationId, requestPeriod)

      if (
        latestParamsRef.current.stationId !== requestStationId ||
        latestParamsRef.current.period !== requestPeriod
      ) {
        return
      }

      const pointMap = new Map<number, Record<string, number | null>>()
      const sourceToKey = new Map<string, string>()
      const keyLabels = new Map<string, string>()

      for (const point of response.points) {
        const timestamp = Date.parse(point.recorded_at)
        if (Number.isNaN(timestamp)) {
          continue
        }

        const rawSource = point.source_url && point.source_url.trim() ? point.source_url : '__default__'
        let dataKey = sourceToKey.get(rawSource)
        if (!dataKey) {
          if (rawSource === '__default__') {
            dataKey = 'listeners'
          } else {
            dataKey = `source_${sourceToKey.size + 1}`
          }
          sourceToKey.set(rawSource, dataKey)
          keyLabels.set(dataKey, rawSource === '__default__' ? 'Listeners' : rawSource)
        }

        const normalizedCount = normalizeCount(point.count)
        const existing = pointMap.get(timestamp) ?? {}
        existing[dataKey] = normalizedCount
        pointMap.set(timestamp, existing)
      }

      if (keyLabels.size === 0) {
        keyLabels.set('listeners', 'Listeners')
      }

      const keys = Array.from(keyLabels.keys()).sort((a, b) => {
        if (a === 'listeners') {
          return -1
        }
        if (b === 'listeners') {
          return 1
        }
        const labelA = keyLabels.get(a) ?? a
        const labelB = keyLabels.get(b) ?? b
        return labelA.localeCompare(labelB)
      })

      const sortedTimestamps = Array.from(pointMap.keys()).sort((a, b) => a - b)
      const dataset: ChartDatasetEntry[] = sortedTimestamps.map((timestamp) => {
        const values = pointMap.get(timestamp) ?? {}
        const entry: ChartDatasetEntry = { time: timestamp }

        for (const key of keys) {
          entry[key] = values[key] ?? null
        }

        return entry
      })

      const hasMultipleSources = keyLabels.size > 1
      const configs: ChartSeriesConfig[] = keys.map((key) => ({
        id: key,
        dataKey: key,
        label: hasMultipleSources ? keyLabels.get(key) ?? key : 'Listeners',
      }))

      setSeries({
        dataset,
        configs,
        lastRefresh: response.last_refresh,
      })
      setError(null)
    } catch (err) {
      if (
        latestParamsRef.current.stationId !== requestStationId ||
        latestParamsRef.current.period !== requestPeriod
      ) {
        return
      }

      const message = err instanceof Error ? err.message : 'Unable to load listener count history.'
      setError(message)
    } finally {
      fetchInFlightRef.current = false
      setLoading(false)
      const shouldRetry = pendingRef.current
      pendingRef.current = false

      if (shouldRetry && fetchHistoryRef.current) {
        window.setTimeout(() => {
          const retry = fetchHistoryRef.current
          if (retry) {
            void retry()
          }
        }, 0)
      }
    }
  }, [active, normalizeCount, selectedPeriod, stationId])

  React.useEffect(() => {
    fetchHistoryRef.current = fetchHistory
  }, [fetchHistory])

  React.useEffect(() => {
    if (!active) {
      resetState()
      return
    }

    const run = fetchHistoryRef.current
    if (run) {
      void run()
    }
  }, [active, resetState, selectedPeriod, stationId])

  React.useEffect(() => {
    if (!active) {
      lastAppliedUpdateRef.current = null
      return
    }

    if (!lastUpdated || lastAppliedUpdateRef.current === lastUpdated) {
      return
    }

    lastAppliedUpdateRef.current = lastUpdated
    const run = fetchHistoryRef.current
    if (run) {
      void run()
    }
  }, [active, lastUpdated])

  React.useEffect(() => {
    if (!active) {
      lastAppliedVersionRef.current = version
      return
    }

    if (lastAppliedVersionRef.current === null) {
      lastAppliedVersionRef.current = version
      return
    }

    if (version === lastAppliedVersionRef.current) {
      return
    }

    lastAppliedVersionRef.current = version
    const run = fetchHistoryRef.current
    if (run) {
      void run()
    }
  }, [active, version])

  React.useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (!active) {
      return undefined
    }

    const now = Date.now()
    const target = nextRefreshAt ?? now + LISTENER_COUNT_REFRESH_INTERVAL_MS
    const delay = Math.min(
      LISTENER_COUNT_REFRESH_INTERVAL_MS,
      Math.max(1000, target + 1000 - now),
    )

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      const run = fetchHistoryRef.current
      if (run) {
        void run()
      }
    }, delay)

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [active, nextRefreshAt])

  React.useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    },
    [],
  )

  const handlePeriodChange = React.useCallback(
    (_: React.MouseEvent<HTMLElement>, value: ListenerCountHistoryPeriod | null) => {
      if (!value || value === selectedPeriod) {
        return
      }

      setSelectedPeriod(value)
    },
    [selectedPeriod],
  )

  const hasData = React.useMemo(() => {
    if (!series.dataset.length || !series.configs.length) {
      return false
    }

    return series.configs.some((cfg) =>
      series.dataset.some((point) => point[cfg.dataKey] !== null && point[cfg.dataKey] !== undefined),
    )
  }, [series.configs, series.dataset])

  const lastRefreshRelative = series.lastRefresh
    ? formatListenerCountRelativeTime(series.lastRefresh)
    : null
  const lastRefreshAbsolute = series.lastRefresh ? new Date(series.lastRefresh) : null

  const periodSelector = (
    <ToggleButtonGroup
      value={selectedPeriod}
      exclusive
      onChange={handlePeriodChange}
      color="primary"
      size="small"
      sx={{
        flexWrap: 'wrap',
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        '& .MuiToggleButton-root': {
          flexGrow: { xs: 1, sm: 0 },
        },
      }}
    >
      {PERIOD_OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          disabled={!active || (loading && option.value !== selectedPeriod)}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )

  let body: React.ReactNode
  let lastRefreshLabel = ''

  if (!stationId) {
    lastRefreshLabel = 'Waiting for station selection…'
    body = (
      <Typography color="text.secondary">
        Choose a station from the header to review listener history.
      </Typography>
    )
  } else if (!configLoaded) {
    lastRefreshLabel = 'Waiting for station configuration…'
    body = (
      <Stack spacing={1.5} sx={{ mt: 0.5 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
        <Skeleton variant="text" width="60%" />
      </Stack>
    )
  } else if (!configEnabled || !configEndpoint) {
    lastRefreshLabel = 'Listener counts are disabled for this station.'
    body = (
      <Stack spacing={1.5} sx={{ mt: 0.5 }}>
        <Typography color="text.secondary">
          Listener counts are disabled for this station. Enable them from station settings to chart
          trends over time.
        </Typography>
        <Button component={RouterLink} to="/settings/general" variant="outlined" size="small">
          Open settings
        </Button>
      </Stack>
    )
  } else {
    if (series.lastRefresh) {
      lastRefreshLabel = lastRefreshRelative ? `Last refresh ${lastRefreshRelative}` : 'Last refresh —'
      if (lastRefreshAbsolute) {
        lastRefreshLabel = `${lastRefreshLabel} (${lastRefreshAbsolute.toLocaleString()})`
      }
    } else {
      lastRefreshLabel = 'Waiting for history updates…'
    }

    body = (
      <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
        {error ? (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        ) : null}
        <Box sx={{ position: 'relative', minHeight: 180 }}>
          {hasData ? (
            <LineChart
              dataset={series.dataset}
              height={200}
              margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
              xAxis={[
                {
                  dataKey: 'time',
                  scaleType: 'time',
                  valueFormatter: (value) => formatTimestamp(value as number),
                },
              ]}
              yAxis={[
                {
                  valueFormatter: (value) => formatCount(value as number | null),
                },
              ]}
              series={series.configs.map((cfg) => ({
                id: cfg.id,
                label: cfg.label,
                dataKey: cfg.dataKey,
                connectNulls: true,
                showMark: false,
              }))}
              slotProps={{
                legend: { hidden: series.configs.length <= 1 },
                tooltip: {
                  valueFormatter: (value) => formatCount(value as number | null),
                  labelFormatter: (value) => formatTimestamp(value as number),
                },
              }}
            />
          ) : loading ? (
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
          ) : (
            <Box
              sx={{
                height: 200,
                borderRadius: 3,
                border: (theme) => `1px dashed ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                typography: 'body2',
              }}
            >
              No listener count history for this period yet.
            </Box>
          )}
          {loading && hasData ? (
            <Box
              sx={{
                position: 'absolute',
                top: (theme) => theme.spacing(1),
                right: (theme) => theme.spacing(1),
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={20} thickness={5} />
            </Box>
          ) : null}
        </Box>
      </Stack>
    )
  }

  const headingStyles = React.useMemo(() => dashboardCardHeadingTypography(theme), [theme])

  return (
    <Paper
      elevation={6}
      sx={{ px: 2, py: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
        }}
      >
        <Tooltip
          title={lastRefreshLabel}
          disableHoverListener={!lastRefreshLabel}
          disableFocusListener={!lastRefreshLabel}
          disableTouchListener={!lastRefreshLabel}
        >
          <Typography component="h2" sx={{ flexShrink: 0, ...headingStyles }}>
            Listener count history
          </Typography>
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            flexGrow: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {periodSelector}
        </Box>
      </Box>
      {body}
    </Paper>
  )
}

export default ListenerCountHistoryCard
